import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BUSINESS, whatsappLink } from "../../data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

const Navbar = ({ bannerOffset = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
      data-testid="site-navbar"
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        bannerOffset ? "top-9" : "top-0"
      } ${
        scrolled
          ? "bg-bone/85 backdrop-blur-md border-b border-ink/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8 lg:px-12">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex flex-col leading-none"
        >
          <span className="font-display text-2xl tracking-tight sm:text-[26px]">
            Arjun <span className="text-henna">Mehndi</span> Arts
          </span>
          <span className="mt-0.5 text-[9px] uppercase tracking-[0.35em] text-ink/50">
            Since 2003 · Pathankot
          </span>
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-sm uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-henna" : "text-ink/80 hover:text-ink"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-btn"
            className="hidden rounded-full border border-ink px-6 py-2.5 text-xs uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bone sm:inline-block"
          >
            Book Now
          </a>
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            className="md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-7 w-7 text-ink" />
          </button>
        </div>
      </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
            className="fixed inset-0 z-[100] flex flex-col px-6 py-6"
            style={{ backgroundColor: "#F0E4CE" }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-2xl">{BUSINESS.shortName}</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="h-7 w-7 text-ink" />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                >
                  <NavLink
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className="font-serif text-5xl text-ink"
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto rounded-full bg-henna py-4 text-center text-sm uppercase tracking-[0.2em] text-bone"
              data-testid="mobile-book-btn"
            >
              Book on WhatsApp
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
